import React, { FC, useState, useEffect, useRef, useContext } from 'react'
import { observer } from 'mobx-react'
import assert from 'assert'
import _debug from 'debug'
import { t } from 'ttag'
import _ from 'underscore'

import { Root } from '../../models3/Root'
import './EnhancedResources.css'
import { EnhancedResources, ERDiv, ERSpan } from '../../resources/EnhancedResources'
import { displayError } from '../utils/Errors'
import Modal from 'react-bootstrap/lib/Modal'
import ERTermModal from './ERTermModal'
import { PassageHighlight, ProjectTerm } from '../../models3/ProjectModels'
import { RefRange } from '../../scrRefs/RefRange'
import { ReferenceInput } from '../utils/ReferenceInput'
import RBody from './ResourceViewer'
import { ADBVersion, ApiDotBible } from '../../models3/ApiDotBible'
import { fmt } from '../utils/Fmt'
import { displayableBookNames } from '../../scrRefs/bookNames'
import { StarButton } from '../utils/Buttons'
import { useResumableRecording } from '../utils/useResumableRecording'
import { RootContext } from '../app/RootContext'
import { iso639dash2to3 } from '../utils/Languages'
import { ignoredLemmas } from '../../scrRefs/Lemmas'

const log = _debug('sltt:EnhancedResourcesViewer')

/*
    EnhancedResourcesViewer
        ERTermModal - show info for user selected term
            ERTermView - show info from Lexicon for term
 */

class HighlightRange {
    constructor(public firstId: string, public lastId: string) {
    }
}

interface IEnhancedResourcesViewer {
    rt: Root,
}

function isSLTTReference(resource: string) {
    // The resources from api.bible are 19 characters long, e.g. 880ff5e2c6bd8944-01.
    // The SLTT resources are human readable short names, e.g. NRS89.
    return (resource.length !== 19)
}

const EnhancedResourcesViewer: FC<IEnhancedResourcesViewer> = observer(({ rt }) => {
    const ENHANCED_RESOURCE = 'enhancedResource'

    let initialResource = rt.getDefault(ENHANCED_RESOURCE) || 'NRS89'
    let [resource, _setResource] = useState(initialResource)
    let [highlightRange, setHighlightRange] = useState<HighlightRange | null>(null)

    let [refs, setRefs] = useState<RefRange[]>([])
    const [errored, setErrored] = useState(false)

    const { resumableRecording } = useResumableRecording()

    let setResource = (resource: string) => {
        rt.setDefault(ENHANCED_RESOURCE, resource)
        _setResource(resource)
    }

    const dbsRefs = rt.dbsRefs // to force rerender when dbsRefs changes

    let defaultReferenceId = ENHANCED_RESOURCE+ 'Reference'

    let refInput = rt

    let _displayableBookNames = displayableBookNames(rt.project)

    return (
        <div className="er-viewer"
            onKeyDown={(event) => {
                // We stop propagation of keydown events so that they do not affect the main window.
                // However if we are recording we must let them pass through to the resumable recorder.
                if (!resumableRecording) {
                    event.stopPropagation()
                }
            }}>
            <div className="er-viewer-toolbar">
                <div className="er-viewer-reference-editor">
                    <div className="er-reference-input">
                        <ReferenceInput {...{ refInput, refs, setRefs, defaultReferenceId, 
                            errored, setErrored, includeGotoReferenceButton: true } }/>
                    </div>
                </div>
                {isSLTTReference(resource) && (
                    <div className='er-highlight-buttons'>
                        <span>
                            <HighlightButton {...{ rt, highlightRange, resource, setHighlightRange, color: 1 }} />
                            <HighlightButton {...{ rt, highlightRange, resource, setHighlightRange, color: 2 }} />
                            <HighlightButton {...{ rt, highlightRange, resource, setHighlightRange, color: 3 }} />
                            <HighlightButton {...{ rt, highlightRange, resource, setHighlightRange, color: 0 }} />
                        </span>
                    </div>
                )}                
                <div className="er-viewer-resource-selector">
                    <ERResourceSelector {...{resource, setResource}} />
                </div>
            </div>
            <div className="er-viewer-body">
                {isSLTTReference(resource) ?
                    <ERBody {...{ rt, resource, refs, setHighlightRange}} /> :
                    <RBody {...{ 
                        resourceId: resource, 
                        refs, 
                        dbsRefs,
                        displayableBookNames: _displayableBookNames 
                    }} />
                }
            </div>
        </div>
    )
})

/** List of all enhanced resources available.
 * If api.bible does not have any texts for a language, you must include the languageName for the ER
 * so that we can create a list for that language.
 * 'id' is the 3 letter iso code for the language.
 */
const enhancedResources = [
    { value: 'ANGM', id: 'njo', 
        languageName: 'Ao Naga', text: 'Ao Naga CL Bible - SQC Edition (BSI)' },
    { value: 'CU2010S', id: 'cmn', text: '和合本修订版（简体字）上帝版 (Revised Chinese Union Version, Simplified Script)' },
    { value: 'CU2010T', id: 'cmn', text: '和合本修訂版（繁體字）上帝版 (Revised Chinese Union Version, Traditional Script)' },
    { value: 'BSB', id: 'eng', text: 'Berea Study Bible' },
    { value: 'ESV16UK', id: 'eng', text: 'English Standard Version 2001' },
    { value: 'GNTUK', id: 'eng', text: 'Good News Translation 1994' },
    { value: 'KJV', id: 'eng', text: 'King James Version' },
    { value: 'NIV84', id: 'eng', text: 'New International Version® 1984' },
    { value: 'NRS89', id: 'eng', text: 'New Revised Standard' },
    { value: 'RSV52', id: 'eng', text: 'Revised Standard Version 1952' },
    { value: 'LSG10', id: 'fra', text: 'Louis Segond 1910' },
    { value: 'NBS11', id: 'fra', text: 'Nouvelle Bible Segond' },
    { value: 'TOB10', id: 'fra', text: 'Traduction Œcuménique de la Bible 2010' },
    { value: 'UBSGNT5', id: 'grc', text: 'UBS Greek NT 5' },
    { value: 'HCB', id: 'hau', text: 'Littafi Mai Tsarki, Sabon Rai Don Kowa (Hausa Contemporary Bible)' },
    { value: 'BHS', id: 'hbo', 
        languageName: 'Hebrew, Ancient', text: 'Biblia Hebraica Stuttgartensia' },
    { value: 'HOVM', id: 'hin', text: 'पुराना संस्करण बाइबल (Hindi OV, Re–edited)' },
    { value: 'TBI', id: 'ind', text: 'Alkitab Terjemahan Baru 1974' },
    { value: 'KANM', id: 'kan', text: 'Kannada (Jehovah Version)' },
    { value: 'MOVM', id: 'mal', text: 'മലയാളം പഴയ പതിപ്പ് ബൈബിള് (Malayalam OV)' },
    { value: 'ARA93', id: 'por', text: 'Almeida Revista e Atualizada' },
    { value: 'RSO94', id: 'rus', text: 'Синодальный перевод (Russian Synodal Orthodox Version) 1994' },
    { value: 'RVR60', id: 'spa', text: 'Reina-Valera Revisada' },
    { value: 'SRUV06', id: 'swh', text: 'Union Version ya Kiswahili (Swahili Revised Union Version)' },
    { value: 'TaOVM', id: 'tam', text: 'இலத்தீன் தமிழ் வேதாகமம் (Tamil OV)' },
    { value: 'TeOVM', id: 'tel', text: 'పాత వెర్షన్ బైబిల్ (Telugu OV)' },
    { value: 'TPB08', id: 'tpi', 
        languageName: 'Tok Pisin', text: 'Buk Baibel 2008' },
]

interface IKeyedItem { key: string }

/** If the value is already in the list, return its index.
  * Otherwise, add the value to the list, in alphabetic order, and return its index. */
function addToSortedList(list: IKeyedItem[], value: IKeyedItem) {
    let index = list.findIndex(v => v.key === value.key)
    if (index >= 0) return index

    list.push(value)
    list.sort((a, b) => a.key.localeCompare(b.key))
    index = list.findIndex(v => v.key === value.key)
    return index
}

interface Version {
    name: string, // e.g. 'NIV, New International Version® 1984'
    key: string, // sort key
    value: string, // for api.bible texts, this is the 18(ish) character id, for enhanced resources this is the short name
}

interface VersionsForLanguage {
    languageName: string,
    key: string, // sort key
    languageId: string, // 3 character iso id for this language
    versions: Version[]
}

/** Get versions from api.bible, return empty list on failure 
 * Add these versions to the list versions grouped by language.
*/
async function getApiDotBibleVersions(): Promise<VersionsForLanguage[]> {
    const _versions: VersionsForLanguage[] = []
    
    let adbVersions: ADBVersion[]
    try {
        adbVersions = await ApiDotBible.getBibleVersions()
    } catch (error) {
        displayError(error)
        return _versions
    }
    
    for (let adbVersion of adbVersions) {
        const { name, nameLocal, abbreviation, abbreviationLocal, id } = adbVersion
        const { name: languageName, nameLocal: languageNameLocal, id: languageId } = adbVersion.language

        const _languageName = (languageName !== languageNameLocal) ?
                `${languageNameLocal} (${languageName})` : languageName

        const versionsForLanguage = { 
            languageName: _languageName, 
            key: languageName, // initially sort list by English language name, 
                // otherwise Tamil (in Tamil script) will be hard to find at the bottom of the list.
                // Later we will move the UI language to front of list
            languageId, 
            versions: [] 
        }
        const index = addToSortedList(_versions, versionsForLanguage)
        const vfl = _versions[index]

        const _name = (name !== nameLocal) ? 
            `${abbreviationLocal}, ${nameLocal} (${name})` : `${abbreviationLocal}, ${name}`
        const version = { name: _name, key: _name, value: id }

        addToSortedList(vfl.versions, version)
    }

    return _versions
}

/** Add all the enhanced resources to the list of versions grouped by language */
function addEnhancedResources(versionsForLanguage: VersionsForLanguage[]) {
    for (let er of enhancedResources) {
        const { languageName, id: languageId } = er
        if (languageName) {
            const language = { languageName, key: languageName, languageId, versions: [] }
            addToSortedList(versionsForLanguage, language)
        }
    }

    for (let er of enhancedResources) {
        const _index = versionsForLanguage.findIndex(v => v.languageId === er.id)
        if (_index < 0) {
            log(`### addEnhancedResources: language ${er.id} not found for ${er.text}`)
            continue
        }

        const vfl = versionsForLanguage[_index]
        
        // add this resource to the list
        let key = `+++${er.value}` // sort to front of list, this value is not seen by the user
        const name = `${er.value}+, ${er.text} (${t`Enhanced Resource`})`

        addToSortedList(vfl.versions, { name, key, value: er.value } as IKeyedItem)
    }
}

/** Combine versions from api.bible with Enhanced Resources in SLTT resource bucket.
 * Move the UI language to the front of the list of languages.
 * Move Enhanced Resources to the front of the list of versions for each language.
 * Return the list of versions grouped by language.
 */
function useVersionsByLanguage() {
    let [versions, setVersions] = useState<VersionsForLanguage[]>([])

    const rt = useContext(RootContext)
    const uiLanguage3 = iso639dash2to3(rt!.uiLanguage) // get 3 letter ui language code

    const fetchVersions = async () => {
        const _versions = await getApiDotBibleVersions()
        addEnhancedResources(_versions)
        
        // move UI language to front of list of languages
        const index = _versions.findIndex(v => v.languageId === uiLanguage3)
        if (index >= 0) {
            const uiLanguageVersions = _versions.splice(index, 1)[0]
            _versions.unshift(uiLanguageVersions)
        } else {
            log(`### useVersionsByLanguage: UI language ${uiLanguage3} not found`)
        }

        setVersions(_versions)
    }

    useEffect(() => { fetchVersions() }, [uiLanguage3])

    return versions
}

interface IERResourceSelector {
    resource: string, 
    setResource: (resource: string) => void
}

const ERResourceSelector: FC<IERResourceSelector> = observer(({ resource, setResource }) => {
    const versionsByLanguage = useVersionsByLanguage()
    
    return (
        <div className='app-selector-icon'>
            <select
                    className='er-resources-select'
                    value={resource}
                    onChange={e => { setResource(e.target.value) }} >
                {versionsByLanguage.map(vbl => (
                    <optgroup label={vbl.languageName} key={vbl.languageName}>
                        {vbl.versions.map(version => (
                            <option value={version.value} key={version.value}>
                                {version.name}
                            </option>
                        ))}
                    </optgroup>
                ))}
            </select>
        </div>
    )
})

function findSelectedSpanIds(dbsRefs: RefRange[], erDivs: ERDiv[]) {
    return erDivs
        .flatMap(div => div.spans)
        .filter(span => dbsRefs.some(ref => span.includedIn(ref)))
        .map(span => span.id ?? '???')   
}

interface IERBody {
    rt: Root,
    setHighlightRange: (highlightRange: HighlightRange | null) => void,
    resource: string,
    refs: RefRange[],
}

const ERBody: FC<IERBody> = ({ rt, resource, refs, setHighlightRange }) => {
    let [termId, setTermId] = useState('')
    let [erDivs, setErDivs] = useState<ERDiv[]>([])
    let latestResourceRef = useRef<string>('')
    let [selectedSpanIds, setSelectedSpanIds] = useState<string[]>([])

    useEffect(() => {
        const fetchERDivs = async () => {
            if (!refs.length) return

            let refsString = refs.map(r => r.startRef + r.endRef).toString()
            let resourceRef = resource + refsString
            latestResourceRef.current = resourceRef
            try {
                let _erDivs = await EnhancedResources.fetchRefs(resource, refs)

                // Make sure the last call to this function is the one that takes precedence
                // so we avoid a race condition.
                if (resourceRef === latestResourceRef.current) {
                    setSelectedSpanIds(findSelectedSpanIds(rt.dbsRefs, _erDivs))
                    setErDivs(_erDivs)
                }
            } catch (error) {
                log('###', error)
                if (resourceRef === latestResourceRef.current) {
                    setErDivs([])
                }
            }
        }

        fetchERDivs()
    }, 
    [resource, refs, rt.dbsRefs])
    
    // Whenever the selectedSpanIds change, scroll to the first selected span
    useEffect(() => {
        const id = selectedSpanIds[0] ?? '*dont-scroll*'
        log(`scrollIntoView ${id}`)
        const element = document.querySelector(`[data-marbleid="${id}"]`)
        if (element) element.scrollIntoView({ behavior: 'smooth' })
    }, [selectedSpanIds])

    return (
        <div>
            {termId && <ERTermModal {...{ rt, termId, setTermId }} />}
            <ERDivs {...{ rt, erDivs, setHighlightRange, setTermId, resource, selectedSpanIds }} />
        </div>
    )
}


interface IHighlightButton {
    rt: Root,
    highlightRange: HighlightRange | null,
    setHighlightRange: (highlightRange: HighlightRange | null) => void,
    color: number,
    resource: string,
}

const HighlightButton: FC<IHighlightButton> = 
        ({ rt, highlightRange, setHighlightRange, resource, color }) => {
    const setColor = (color: number) => {
        if (!highlightRange) return

        rt.passageVideo?.addHighlight(
                color, 
                highlightRange.firstId, 
                highlightRange.lastId, 
                resource)
            .then(() => setHighlightRange(null))
            .catch(displayError)
    }

    return (
        <button className={`er-highlight-button er-highlight-${color}${highlightRange ? '' : '-disabled'}`}
                onClick={() => { setColor(color) }} /> 
        )
}


interface IERDivs {
    rt: Root,
    erDivs: ERDiv[],
    resource: string,
    setHighlightRange: (highlightRange: HighlightRange | null) => void
    setTermId: (resource: string) => void,
    selectedSpanIds: string[],
}

const ERDivs: FC<IERDivs> = ({ rt, erDivs, setHighlightRange, setTermId, resource, selectedSpanIds }) => {
    let highlights = rt.passageVideo?.highlights ?? []

    let className = ''
    if (resource === 'BHS') {
        className = 'bhs-text'
    } else if (resource === 'UBSGNT5') {
        className = 'ubsgnt5-text'
    }

    return (
        <div className={`er er-divs ${className}`}
                dir={resource === 'BHS' ? 'rtl' : undefined}
                onMouseUp={(e: any) => erDivsMouseUpHandler(e, setHighlightRange)} >
            {erDivs.map((erDiv, key) => (
                <ERDivView {...{ rt, erDiv, setTermId, key, highlights, resource, previousErDiv: key > 0 ? erDivs[key-1] : null, selectedSpanIds }} />
            ))}
        </div>
    )
}

function getMarbleId(node: any) {
    while (node) {
        let id = node.getAttribute && node.getAttribute('data-marbleid')
        if (id) return { node, id }
        node = node.parentNode
    }

    return { node: undefined, id: undefined }
}

function erDivsMouseUpHandler(e: any, 
        setHighlightRange: (highlightRange: HighlightRange | null) => void) {

    const selectionObj = window.getSelection()
    if (!selectionObj) {
        setHighlightRange(null)
        return
    }

    // if start and end of selection are the same no text was selected.
    // Nothing to highlight, return
    if (selectionObj.anchorNode === selectionObj.focusNode &&
        selectionObj.anchorOffset === selectionObj.focusOffset) {
        log('onMouseUp empty-selection')
        setHighlightRange(null)
        return
    }

    let { node: firstNode, id: firstId } = getMarbleId(selectionObj.anchorNode)
    const { node: lastNode, id: lastId } = getMarbleId(selectionObj.focusNode)

    // If both notes are from the same paragraph and that paragraph is a header
    // or reference paragraph then the user has not selected any body text.
    // Ignore the selection.
    if (firstNode && (firstNode === lastNode)) {
        let classNames = firstNode.className.split(' ')
        if (classNames.includes('er-s') || classNames.includes('er-r')) {
            setHighlightRange(null)
            return
        }
    }

    if (firstId && lastId) {
        setHighlightRange(new HighlightRange(firstId, lastId))
    } else {
        setHighlightRange(null)
    }
}


interface IERDivView {
    rt: Root,
    erDiv: ERDiv,
    highlights: PassageHighlight[],
    setTermId: (id: string) => void,
    resource: string,
    previousErDiv: ERDiv | null,
    selectedSpanIds: string[],
}

const ERDivView: FC<IERDivView> = ({ erDiv, highlights, setTermId, resource, rt, previousErDiv, selectedSpanIds }) => {
    // Divide spans up into groups that all have the same value for highlighting
    let spanGroups: SpanGroup[] = []

    erDiv.spans.forEach(span => {
        let color = PassageHighlight.highlighted(highlights, span.id || '', resource)
        let lastGroup = spanGroups.slice(-1)[0]

        if (!lastGroup || lastGroup.color !== color) {
            let newGroup = new SpanGroup()
            newGroup.color = color
            newGroup.spans.push(span)
            spanGroups.push(newGroup)
        } else {
            lastGroup.spans.push(span)
        }
    })

    let heading = ''
    if (previousErDiv && erDiv.bbbccc !== previousErDiv.bbbccc) {
        let BBB = erDiv.bbbccc.slice(0, 3)
        let CCC = erDiv.bbbccc.slice(3, 6)
        let rr = new RefRange(BBB, BBB)
        heading = `${rt.displayableReferences([rr])} ${parseInt(CCC)}`
    }

    return (
        <div data-marbleid={erDiv.id} className={`er-${erDiv.style}`}>
            {heading && <div className="er-new-chapter">{heading}</div>}
            {spanGroups.map((spanGroup, key) => (
                <ERSpanGroup {...{ rt, spanGroup, key, setTermId, selectedSpanIds }} />))}
        </div>
    )
}

class SpanGroup {
    color = 0
    spans: ERSpan[] = []
}


// A list of spans with the same background color

interface IERSpanGroup {
    rt: Root,
    spanGroup: SpanGroup,
    setTermId: (id: string) => void,
    selectedSpanIds: string[],
}

const ERSpanGroup: FC<IERSpanGroup> = ({ spanGroup, setTermId, rt, selectedSpanIds }) => {
    return (
        <span className={`er-highlight-${spanGroup.color}`}>
            {spanGroup.spans.map((span, key) => (
                <ERSpanView {...{ rt, span, key, setTermId, selectedSpanIds }} />
            ))}
        </span>
    )
}


// A span representing a word or very short phrase in the enhanced resource.
// If it has lexical_links you can click on it to see info for that term.
// If it has image_links we display an image icon to click to see the image.

interface IERSpanView {
    rt: Root,
    span: ERSpan,
    setTermId: (id: string) => void,
    selectedSpanIds: string[],
}

const ERSpanView: FC<IERSpanView> = ({ span, setTermId, rt, selectedSpanIds }) => {
    let isSelected = selectedSpanIds.includes(span.id ?? '*no_id*')
    let isImage = span.attributes && span.attributes.image_links

    if (span.type === 'verse') {
        return (
            <>
                <span data-marbleid={span.id || ''} className={`er-v ${isSelected ? 'er-selected' : ''}`}>
                    {span.verse}
                </span>
                {isImage && <ERImageView span={span} /> /* if verse has an associated image(s), show them */}
            </>
        )
    }

    let postText = ''
    let text = span.text || ''
    if (text.endsWith(' ')) {
        text = text.slice(0, -1)
        postText = ' '
    }

    return (
        <span data-marbleid={span.id || ''} className={isSelected ? 'er-selected' : ''}>
            {isImage && <ERImageView span={span} />}
            <ERTextView
                span={span}
                setTermId={setTermId}
                getProjectTerm={rt.project.getProjectTerm.bind(rt.project)}
            />
            <ERKeyTermLinks
                span={span}
                setTermId={setTermId}
                getProjectTerm={rt.project.getProjectTerm.bind(rt.project)}
            />
            {postText}
        </span>
    )
}

type ERKeyTermLinksProps = {
    span: ERSpan,
    setTermId: (id: string) => void,
    getProjectTerm: (id: string) => ProjectTerm | undefined,
}

const ERKeyTermLinks = ({ span, setTermId, getProjectTerm }: ERKeyTermLinksProps) => {
    function doesLinkToKeyTerm(termId: string) {
        return getProjectTerm(termId)?.isKeyTerm ?? false
    }

    let termIds = span.getLexicalLinks()

    return (
        <>
            { termIds.filter(doesLinkToKeyTerm).map((termId) => (
                // Is it okay to use termId as a key? AFAICT, each item in this list links
                // to a different term.
                <StarButton
                    isHollow={false}
                    key={termId}
                    enabled={true}
                    onClick={() => setTermId(termId)}
                    className='star-icon'
                    tooltip=''
                />
            ))}
        </>
    )
}

function removeIgnoredLemmas(termId: string) {
    return termId.split(';')
        .filter(ti => !ignoredLemmas.has(ti.split(':')[1]))
        .join(';')
}

type ERTextViewProps = {
    span: ERSpan,
    setTermId: (id: string) => void,
    getProjectTerm: (id: string) => ProjectTerm | undefined,
}

const ERTextView = ({ span, setTermId, getProjectTerm }: ERTextViewProps) => {
    let text = span.text || ''
    if (text.endsWith(' ')) {
        text = text.slice(0, -1)
    }

    let termIds = span.getLexicalLinks()

    return (
        <>
            { termIds.map((termId, index) => {
                let _text = text
                if (index !== 0) {
                    _text = '*'
                }

                termId = removeIgnoredLemmas(termId)

                // Is it okay to use termId as a key? AFAICT, this component only displays
                // a single link to a single term or several links to different terms, but
                // not several links to the same term.
                if (termId.trim() === '') {
                    return (
                        <ERPlainText key={termId} text={_text} />
                    )
                }

                return (
                    <ERLink
                        key={termId}
                        text={_text}
                        termId={termId}
                        spanType={span.type}
                        getProjectTerm={getProjectTerm}
                        setTermId={setTermId}
                    />
                )
            })}
        </>
    )
}

type ERPlainTextProps = {
    text: string,
}

const ERPlainText = ({ text }: ERPlainTextProps) => {
    return (
        <span className={`er-text`}>
            { text }
        </span>
    )
}

type ERLinkProps = {
    text: string,
    termId: string,
    spanType: string,
    getProjectTerm: (lexicalLink: string) => ProjectTerm | undefined,
    setTermId: (id: string) => void,
}

const ERLink = ({ text, termId, spanType, getProjectTerm, setTermId }: ERLinkProps) => {
    let term = getProjectTerm(termId)

    let className = `er-${spanType}`
    if (term?.hasVideo()) {
        className += ' er-sign-video-present'
    }

    return (
        <span className={className}
            onClick={() => {
                // termId = "SDBG:γένεσις:000001|SDBG:γένεσις:000002"
                log('ERSpanView click', JSON.stringify(termId.split(':')))
                setTermId(termId)
            }}>
            { text }
        </span>
    )
}

interface IERImageView {
    span: ERSpan,
}

const ERImageView: FC<IERImageView> = ({ span }) => {
    let [imageModalOpen, setImageModalOpen] = useState(false)

    let imagePaths = span.attributes.image_links.split('|')

    return (
        <span>
            {imageModalOpen && <ERImageModal {...{imagePaths, setImageModalOpen}} />}
            <span className="er-image fa-fw fa-image" onClick={() => setImageModalOpen(true)} />
        </span>
    )
}

// Modal dialog to display an image associated with a word.

interface IERImageModal {
    imagePaths: string[],
    setImageModalOpen: (open: boolean) => void,
}

const ERImageModal: FC<IERImageModal> = ({ imagePaths, setImageModalOpen }) => {
    let basePath = 'https://s3.amazonaws.com/sltt-resources/SLMARBLE/images'

    return (
        <Modal style={{ top: '1%' }}
            bsSize="large"
            show={true}
            onHide={() => setImageModalOpen(false)} >
            <Modal.Header closeButton> </Modal.Header>
            <Modal.Body>
                <div className="er-images-div">
                    {imagePaths.map((ip, key) => (
                        <div key={key}>
                            <img className="er-image-display"
                                src={`${basePath}/${ip}`} />
                        </div>
                    ))}
                </div>
            </Modal.Body>
        </Modal>
    )
}

export default EnhancedResourcesViewer
